// Nástroje pro vyčištění cache — použité v sekci Administrace na úvodní stránce (jen pro adminy)
// a zároveň vystavené na window.* pro rychlé volání z konzole prohlížeče.
//
// Každá funkce dělá dvě věci:
//   1. smaže SERVEROVOU cache (memory + offline soubory) přes DELETE /api/_cache/... — vyžaduje admina
//   2. zneplatní KLIENTSKOU cache (React Query dotazy v paměti + perzistentní stromy v IndexedDB)
import { queryClient } from '../api/queryClient'
import { api } from '../api/client'
import { clearGroupTreeCache } from './groupTreeCache'

// React Query cache — klíče podle useAppData.js / useStationGroups.js
const STATION_QUERY_KEYS = [
  ['allStations'],
  ['stationHierarchy'],
]
const CODEBOOK_QUERY_KEYS = [
  ['elements'],
  ['stationTypes'],
  ['obsTypes'],
  ['basins'],
  ['stationOwners'],
]
const GROUP_QUERY_KEYS = [
  ['stationGroupTypes'],
  ['stationGroupSources'],
  ['stationGroupsBySource'],
  ['stationGroupsBySources'],
  ['stationGroupMembers'],
  ['saved-station-groups'],
  ['saved-station-group-filter'],
  ['saved-station-group-filters'],
]

// Názvy serverových route cache (viz createRouteCache('<name>') v cw-server/src/routes/*.js)
const STATION_SERVER_CACHES  = ['geography', 'station_hierarchy', 'station_owner', 'observation']
const CODEBOOK_SERVER_CACHES = ['elements', 'station_type', 'obs_type', 'basin']
const GROUP_SERVER_CACHES    = [
  'station_group', 'station_group_member', 'station_group_source', 'station_group_type',
  'saved_station_group',
]

function removeQueries(keys) {
  for (const key of keys) queryClient.removeQueries({ queryKey: key })
}

// Smaže serverovou cache pro zadané route. Vrací počet smazaných offline souborů.
// Route bez naplněné cache server přeskočí; 404 nastane jen když neexistuje ani jedna z nich.
async function clearServerCaches(names) {
  const res = await api.delete(`/_cache/${names.join(',')}`)
  return res?.diskFiles ?? 0
}

// Cache stanic (geografie, hierarchie, majitelé, dostupnost pozorování)
export async function clearStationCache() {
  const diskFiles = await clearServerCaches(STATION_SERVER_CACHES)
  removeQueries(STATION_QUERY_KEYS)
  return diskFiles
}

// Číselníky (veličiny, typy stanic, obs_type, povodí)
export async function clearCodebookCache() {
  const diskFiles = await clearServerCaches(CODEBOOK_SERVER_CACHES)
  removeQueries(CODEBOOK_QUERY_KEYS)
  return diskFiles
}

// Cache skupin: serverové route cache + RQ dotazy + perzistentní stromy v IndexedDB
export async function clearGroupCache() {
  const diskFiles = await clearServerCaches(GROUP_SERVER_CACHES)
  removeQueries(GROUP_QUERY_KEYS)
  const trees = await clearGroupTreeCache()
  return { diskFiles, trees }
}

// Úplný reset — všechny serverové cache + všechny RQ dotazy + IndexedDB stromy
export async function clearAllClientCache() {
  const res = await api.delete('/_cache')
  queryClient.clear()
  const trees = await clearGroupTreeCache()
  return { cleared: res?.cleared ?? 'all', trees }
}

// Vystav helpery na window pro volání z konzole
if (typeof window !== 'undefined') {
  window.clearGroupTreeCache = clearGroupTreeCache
  window.clearGroupCache = clearGroupCache
  window.clearStationCache = clearStationCache
  window.clearCodebookCache = clearCodebookCache
  window.clearAllClientCache = clearAllClientCache
}
